import { useEffect, useState, type ReactNode } from 'react';
import { Crosshair, Hammer, User, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/Switch';
import type { AvatarConfig, Facing } from '@/features/digitalOffice/avatars';
import { zoneLabelAt } from '@/features/digitalOffice/map';
import {
  useLiveStoreVersion,
  type OfficeLiveStore,
  type PresenceStatus,
} from '@/features/digitalOffice/presence';
import { formatClock } from '@/features/workMode/api';
import { AvatarPreview } from './AvatarPreview';

export interface RosterPerson {
  userId: string;
  name: string;
  jobTitle: string | null;
  /** Null when the person has never created a character. */
  config: AvatarConfig | null;
  /** Last persisted position (offline silhouette location). */
  position: { x: number; y: number; facing: Facing } | null;
  lastSeenAt: string | null;
}

/** Someone signed in at a Work Mode bench right now. */
export interface RosterBench {
  userId: string;
  name: string;
  config: AvatarConfig;
  stationName: string;
  itemName: string | null;
  unitsDone: number;
  unitStartedAt: number;
  onBreak: boolean;
  breakLabel: string | null;
}

interface OfficeRosterProps {
  store: OfficeLiveStore;
  meId: string;
  myName: string;
  myConfig: AvatarConfig;
  myStatus: PresenceStatus;
  people: RosterPerson[];
  benches?: RosterBench[];
  showOffline: boolean;
  onToggleOffline: (v: boolean) => void;
  onFocus: (userId: string) => void;
  /**
   * Privacy mode for screen recordings: rows show "Teammate" / "You"
   * instead of the person's name, and never fall back to initials.
   */
  hideNames?: boolean;
  footer?: ReactNode;
  className?: string;
}

const STATUS_DOT: Record<PresenceStatus | 'offline', string> = {
  online: 'bg-emerald-400 ring-emerald-400/30',
  away: 'bg-amber-400 ring-amber-400/30',
  offline: 'bg-gray-300 ring-gray-300/30',
};

const STATUS_LABEL: Record<PresenceStatus | 'offline', string> = {
  online: 'Online',
  away: 'Away',
  offline: 'Offline',
};

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

/**
 * "Who's here" side panel. Online people (from presence) first, then
 * everyone else from the org as offline. Click a row to pan the camera
 * to that person.
 */
export function OfficeRoster({
  store,
  meId,
  myName,
  myConfig,
  myStatus,
  people,
  benches = [],
  showOffline,
  onToggleOffline,
  onFocus,
  hideNames = false,
  footer,
  className,
}: OfficeRosterProps) {
  useLiveStoreVersion(store);
  const label = (name: string, isMe = false) =>
    hideNames ? (isMe ? 'You' : 'Teammate') : isMe ? `${name} (you)` : name;
  // Zone labels and bench timers change constantly; refresh on a slow tick.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const pinned = new Set(benches.map((b) => b.userId));
  const online = [...store.players.values()]
    .filter((p) => !pinned.has(p.userId))
    .sort((a, b) => a.name.localeCompare(b.name));
  const onlineIds = new Set(online.map((p) => p.userId));
  const offline = people
    .filter((p) => p.userId !== meId && !onlineIds.has(p.userId) && !pinned.has(p.userId))
    .sort((a, b) => a.name.localeCompare(b.name));

  const me = store.me;
  const now = Date.now();

  return (
    <aside
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-xl border border-border bg-card',
        className,
      )}
    >
      {benches.length > 0 && (
        <>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Hammer className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
              <h2 className="text-sm font-medium">On the benches</h2>
            </div>
            <span className="rounded-full bg-bright-moss/30 px-2 py-0.5 text-[11px] text-bright-moss-dark">
              {benches.length} building
            </span>
          </div>
          <ul role="list" className="divide-y divide-border/70 border-b border-border">
            {benches.map((b) => {
              const elapsed = Math.max(0, (now - b.unitStartedAt) / 1000);
              const subtitle = b.onBreak
                ? `${b.stationName} · ${b.breakLabel ?? 'On a break'} · ${b.unitsDone} done`
                : `${b.stationName} · ${b.itemName ? `${b.itemName} ` : ''}#${b.unitsDone + 1} · ${formatClock(elapsed)}`;
              return (
                <RosterRow
                  key={b.userId}
                  name={label(b.name, b.userId === meId)}
                  subtitle={subtitle}
                  status={b.onBreak ? 'away' : 'online'}
                  config={b.config}
                  facing="down"
                  onFocus={() => onFocus(b.userId)}
                  highlight={b.userId === meId}
                  hideInitials={hideNames}
                />
              );
            })}
          </ul>
        </>
      )}

      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
          <h2 className="text-sm font-medium">Around the building</h2>
        </div>
        <span
          title={store.error ?? undefined}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]',
            store.connected ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700',
          )}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              store.connected ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse',
            )}
          />
          {store.connected ? `${online.length + 1} online` : store.error ? 'Reconnecting…' : 'Connecting…'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        <ul role="list" className="divide-y divide-border/70">
          {!pinned.has(meId) && (
            <RosterRow
              name={label(myName, true)}
              subtitle={me ? zoneLabelAt(me.x, me.y) : '—'}
              status={myStatus}
              config={myConfig}
              facing={me?.facing ?? 'down'}
              onFocus={() => onFocus(meId)}
              highlight
              hideInitials={hideNames}
            />
          )}
          {online.map((p) => (
            <RosterRow
              key={p.userId}
              name={label(p.name)}
              subtitle={zoneLabelAt(p.x, p.y)}
              status={store.displayStatus(p)}
              config={p.config}
              facing={p.facing}
              onFocus={() => onFocus(p.userId)}
              hideInitials={hideNames}
            />
          ))}
        </ul>

        {offline.length > 0 && (
          <>
            <div className="flex items-center justify-between bg-secondary/60 px-4 py-2">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Offline · {offline.length}
              </p>
              <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                Show on map
                <Switch
                  checked={showOffline}
                  onCheckedChange={onToggleOffline}
                  className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3"
                  aria-label="Show offline teammates on the map"
                />
              </label>
            </div>
            <ul role="list" className="divide-y divide-border/70">
              {offline.map((p) => (
                <RosterRow
                  key={p.userId}
                  name={label(p.name)}
                  subtitle={
                    p.position
                      ? `${zoneLabelAt(p.position.x, p.position.y)} · ${relativeTime(p.lastSeenAt)}`
                      : p.jobTitle ?? 'Hasn’t visited yet'
                  }
                  status="offline"
                  config={p.config}
                  facing={p.position?.facing ?? 'down'}
                  onFocus={p.position ? () => onFocus(p.userId) : undefined}
                  dim
                  hideInitials={hideNames}
                />
              ))}
            </ul>
          </>
        )}
      </div>
      {footer && <div className="border-t border-border px-4 py-2.5">{footer}</div>}
    </aside>
  );
}

function RosterRow({
  name,
  subtitle,
  status,
  config,
  facing,
  onFocus,
  highlight,
  dim,
  hideInitials,
}: {
  name: string;
  subtitle: string;
  status: PresenceStatus | 'offline';
  config: AvatarConfig | null;
  facing: Facing;
  onFocus?: () => void;
  highlight?: boolean;
  dim?: boolean;
  /** Use a neutral placeholder instead of initials when there is no character. */
  hideInitials?: boolean;
}) {
  const initials = hideInitials
    ? ''
    : name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join('');
  return (
    <li>
      <button
        type="button"
        onClick={onFocus}
        disabled={!onFocus}
        title={onFocus ? `Find ${name} on the map` : undefined}
        className={cn(
          'group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
          onFocus ? 'hover:bg-secondary/70' : 'cursor-default',
          highlight && 'bg-bright-moss/15',
          dim && 'opacity-70',
        )}
      >
        <div className="relative shrink-0">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-secondary">
            {config ? (
              <AvatarPreview config={config} size={40} facing={facing} className={cn(dim && 'opacity-60')} />
            ) : initials ? (
              <span className="text-xs font-medium text-muted-foreground">{initials}</span>
            ) : (
              <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} aria-hidden />
            )}
          </div>
          <span
            className={cn(
              'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ring-2',
              STATUS_DOT[status],
            )}
            aria-label={STATUS_LABEL[status]}
            title={STATUS_LABEL[status]}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-foreground">{name}</p>
          <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>
        </div>
        {onFocus && (
          <Crosshair
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
            strokeWidth={1.6}
          />
        )}
      </button>
    </li>
  );
}
